/** Pure form logic behind WarehousesCard's edit dialog.
 *
 *  Kept out of the component so it can be tested directly: the dialog is built
 *  from Radix primitives, which don't survive happy-dom, and this is the part
 *  where a bug is silent — a dropped field just doesn't get altered, and a
 *  spurious one resizes a customer's warehouse nobody asked to resize.
 */
import type {
  SnowflakeWarehouse,
  SnowflakeWarehouseChanges,
} from '@/services/tenantConfig.service';

export interface SizeOption {
  /** The ALTER token, e.g. XXLARGE. */
  value: string;
  /** What SHOW WAREHOUSES reports, e.g. 2X-Large. */
  label: string;
}

/** Seconds -> "5s" / "2m" / "1h 30m", so the table reads at a glance. */
export function formatAutoSuspend(seconds?: number): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes < 60) return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const leftoverMinutes = minutes % 60;
  return leftoverMinutes ? `${hours}h ${leftoverMinutes}m` : `${hours}h`;
}

/** The option matching a size as Snowflake reported it. SHOW gives the label
 *  form; accept the token form too, since the API round-trips either. */
export function matchSizeOption(
  sizes: SizeOption[],
  reported?: string
): SizeOption | undefined {
  const needle = (reported ?? '').toUpperCase();
  if (!needle) return undefined;
  return sizes.find(
    (s) => s.label.toUpperCase() === needle || s.value.toUpperCase() === needle
  );
}

/** Only what actually differs from the warehouse's current state. The API alters
 *  exactly the properties present, so an unchanged field must not appear. */
export function computeWarehouseChanges(
  warehouse: SnowflakeWarehouse,
  sizes: SizeOption[],
  form: { size: string; autoSuspend: string }
): SnowflakeWarehouseChanges {
  const changes: SnowflakeWarehouseChanges = {};

  const currentSize = matchSizeOption(sizes, warehouse.size)?.value;
  if (form.size && form.size !== currentSize) {
    changes.size = form.size;
  }

  if (form.autoSuspend !== '') {
    const seconds = Number(form.autoSuspend);
    if (Number.isFinite(seconds) && seconds !== warehouse.auto_suspend) {
      changes.auto_suspend = seconds;
    }
  }

  return changes;
}
